import { MediaLibraryItem } from '../../../api/media';
import { AssetPoolItem } from '../assetPool';

export type FolderType = 'all' | 'image' | 'video' | 'audio';
export type SourceFilter = 'all' | 'uploaded' | 'generated';

interface HttpErrorRecord {
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
      detail?: string;
    };
  };
  message?: string;
}

type ErrorContext = 'list' | 'upload' | 'rename' | 'metadata' | 'remove';

/**
 * Above this many files in one drop, the upload stops asking the backend to
 * describe each image for the AI reviewer. Auto-describe is one small vision
 * call per image; on a bulk drop most files are never referenced by a
 * pipeline, so the batch opts out and the user runs Describe on the few that
 * are. The hook sends the opt-out, the Upload tab says so beforehand — one
 * constant so the two cannot disagree about what counts as bulk.
 */
export const BULK_DESCRIBE_THRESHOLD = 5;

export const FOLDER_STYLES: Record<FolderType, string> = {
  all: 'border-[var(--border-strong)] text-[var(--app-text)] bg-[var(--surface-1)] shadow-[2px_2px_0px_0px_var(--border-strong)]',
  image: 'border-[var(--border-strong)] text-[var(--app-text)] bg-[var(--surface-1)] shadow-[2px_2px_0px_0px_var(--border-strong)]',
  video: 'border-[var(--border-strong)] text-[var(--app-text)] bg-[var(--surface-1)] shadow-[2px_2px_0px_0px_var(--border-strong)]',
  audio: 'border-[var(--border-strong)] text-[var(--app-text)] bg-[var(--surface-1)] shadow-[2px_2px_0px_0px_var(--border-strong)]',
};

export const SOURCE_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: 'all', label: 'All Sources' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'generated', label: 'Generated' },
];

export const inferFolderFromType = (type?: string, mimeType?: string): FolderType => {
  const normalizedType = (type || '').toLowerCase();
  const normalizedMime = (mimeType || '').toLowerCase();
  if (normalizedType.includes('image') || normalizedMime.startsWith('image/')) return 'image';
  if (normalizedType.includes('video') || normalizedType.includes('ai_video') || normalizedMime.startsWith('video/')) {
    return 'video';
  }
  if (normalizedType.includes('audio') || normalizedType.includes('music') || normalizedMime.startsWith('audio/')) {
    return 'audio';
  }
  return 'all';
};

export const isGeneratedSource = (source?: string, metadata?: Record<string, unknown>): boolean => {
  const normalized = (source || '').toLowerCase();
  if (
    normalized.includes('generated') ||
    normalized.includes('pipeline') ||
    normalized.includes('checkpoint')
  ) return true;

  // Items persisted to DB may arrive with source="persisted" even when generated.
  // Reliable metadata markers written by the generator pipeline survive the round-trip.
  if (metadata) {
    if (metadata.checkpoint_type === 'generator') return true;
    if (metadata.generated_from_checkpoint_id) return true;
    if (metadata.attachment_origin === 'pipeline_generated_attachment') return true;
  }

  return false;
};

export const inferSourceBucket = (source?: string, metadata?: Record<string, unknown>): SourceFilter => {
  if (isGeneratedSource(source, metadata)) return 'generated';
  return 'uploaded';
};

export const inferUploadType = (file: File): string => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'file';
};

const hasDuplicateNameSignal = (rawText: string): boolean => {
  const normalized = rawText.toLowerCase();
  return (
    normalized.includes('duplicate') ||
    normalized.includes('already exists') ||
    normalized.includes('name exists') ||
    normalized.includes('conflict')
  );
};

export const toActionableError = (error: unknown, context: ErrorContext = 'list'): string => {
  const record = error as HttpErrorRecord | undefined;
  const status = record?.response?.status;
  const details =
    record?.response?.data?.error ||
    record?.response?.data?.message ||
    record?.response?.data?.detail ||
    record?.message;

  if (status === 405) {
    return 'Media-library route is disabled (HTTP 405). Check upload/list route configuration.';
  }
  if (status === 413) {
    return 'This file is too large (HTTP 413). Upload a smaller file or increase backend upload limits.';
  }
  if (context === 'rename' && (status === 409 || hasDuplicateNameSignal(details || ''))) {
    return 'A file with this name already exists. Choose a different name and try again.';
  }
  if (context === 'remove' && status === 409) {
    return 'This file cannot be removed right now due to backend constraints. Try again after dependent jobs finish.';
  }
  if (context === 'rename') {
    return details || 'Failed to rename media item.';
  }
  if (context === 'metadata') {
    return details || 'Failed to save media metadata.';
  }
  if (context === 'upload') {
    return details || 'Failed to upload media file.';
  }
  return details || 'Media library action failed.';
};

const normalizeGeneratedAsset = (asset: AssetPoolItem): MediaLibraryItem | null => {
  const mediaId = (asset.media_id || '').trim();
  const fallbackId = mediaId || (asset.id || '').trim() || (asset.url || '').trim();
  if (!fallbackId) return null;

  return {
    id: fallbackId,
    media_id: fallbackId,
    type: asset.type || asset.kind || 'unknown',
    name: asset.name || fallbackId,
    url: asset.url,
    mime_type: asset.mime_type,
    source: 'generated',
    size_bytes: asset.size_bytes,
    metadata: {
      ...(asset.metadata || {}),
      generated_asset_pool_id: asset.id,
      generated_has_real_media_id: Boolean(mediaId),
      source_run_id: asset.run_id,
      source_checkpoint_id: asset.checkpoint_id,
      source_checkpoint_name: asset.checkpoint_name,
      source_checkpoint_index: asset.checkpoint_index,
    },
  };
};

export const mergeLibraryItems = (libraryItems: MediaLibraryItem[], generatedAssets: AssetPoolItem[]): MediaLibraryItem[] => {
  const merged = new Map<string, MediaLibraryItem>();
  libraryItems.forEach((item) => {
    merged.set(item.media_id || item.id, item);
  });
  generatedAssets
    .map((asset) => normalizeGeneratedAsset(asset))
    .filter((asset): asset is MediaLibraryItem => Boolean(asset))
    .forEach((asset) => {
      const id = asset.media_id || asset.id;
      if (!merged.has(id)) {
        merged.set(id, asset);
      }
    });

  return Array.from(merged.values()).sort((a, b) => {
    const byType = inferFolderFromType(a.type, a.mime_type).localeCompare(inferFolderFromType(b.type, b.mime_type));
    if (byType !== 0) return byType;
    return (a.name || a.media_id || a.id).localeCompare(b.name || b.media_id || b.id);
  });
};

export const filterLibraryItems = (
  items: MediaLibraryItem[],
  folderType: FolderType,
  sourceFilter: SourceFilter,
  searchQuery: string
): MediaLibraryItem[] => {
  const query = searchQuery.trim().toLowerCase();
  return items.filter((item) => {
    const itemFolder = inferFolderFromType(item.type, item.mime_type);
    const isGenerated = isGeneratedSource(item.source, item.metadata);

    if (folderType !== 'all' && itemFolder !== folderType) return false;
    if (sourceFilter === 'uploaded' && isGenerated) return false;
    if (sourceFilter === 'generated' && !isGenerated) return false;
    if (!query) return true;

    return [item.name, item.media_id || item.id, item.type, item.source || 'unknown']
      .join(' ')
      .toLowerCase()
      .includes(query);
  });
};

export const countItemsByFolder = (items: MediaLibraryItem[]): Record<FolderType, number> =>
  items.reduce<Record<FolderType, number>>(
    (acc, item) => {
      const folder = inferFolderFromType(item.type, item.mime_type);
      acc.all += 1;
      if (folder !== 'all') acc[folder] += 1;
      return acc;
    },
    { all: 0, image: 0, video: 0, audio: 0 }
  );

export const selectMediaItems = (items: MediaLibraryItem[], selectedIds: string[]): MediaLibraryItem[] =>
  selectedIds
    .map((id) => items.find((item) => (item.media_id || item.id) === id))
    .filter((item): item is MediaLibraryItem => Boolean(item));

export const readMediaContextDraft = (item: MediaLibraryItem | null): string => {
  if (!item) return '';
  const metadata = item.metadata || {};
  const value = metadata.user_context ?? metadata.context_note ?? metadata.context ?? '';
  return typeof value === 'string' ? value : '';
};

export const readMediaAiDescription = (
  item: MediaLibraryItem | null
): { text: string; model: string; userEdited: boolean } => {
  if (!item) return { text: '', model: '', userEdited: false };
  const metadata = item.metadata || {};
  const text = typeof metadata.ai_description === 'string' ? metadata.ai_description : '';
  const model = typeof metadata.ai_description_model === 'string' ? metadata.ai_description_model : '';
  // The generic metadata PUT round-trips a boolean as the string "true" — treat
  // either as user-edited.
  const rawUserEdited = metadata.ai_description_user_edited;
  const userEdited = rawUserEdited === true || rawUserEdited === 'true';
  return { text, model, userEdited };
};

// ─── Grouped View ────────────────────────────────────────────────────────────

export interface MediaGroup {
  key: string;     // unique group key: 'clip:{id}', 'pipeline:{run_id}', 'uploaded', 'generated'
  label: string;   // display label
  date?: string;   // ISO date string for the earliest item in the group
  items: MediaLibraryItem[];
}

function clipLabel(clipId: string, clipNames: Record<string, string>): string {
  return clipNames[clipId] || `Clip ${clipId.slice(0, 8)}`;
}

export function groupMediaItems(
  items: MediaLibraryItem[],
  clipNames: Record<string, string> = {},
): MediaGroup[] {
  const groupMap = new Map<string, MediaGroup>();

  for (const item of items) {
    const metaPipelineRunId =
      (item.metadata?.pipeline_run_id as string) || item.pipeline_run_id || '';
    const metaPipelineName =
      (item.metadata?.pipeline_name as string) || item.pipeline_name || '';
    const clipId = item.clip_id || '';
    const source = item.source || '';

    let key: string;
    let label: string;
    let date: string | undefined = item.created_at;

    if (metaPipelineRunId) {
      key = `pipeline:${metaPipelineRunId}`;
      label = metaPipelineName || 'Pipeline';
    } else if (clipId) {
      key = `clip:${clipId}`;
      label = clipLabel(clipId, clipNames);
    } else if (source === 'upload_pool' || source === 'manual_upload' || source === 'uploaded') {
      key = 'uploaded';
      label = 'Uploaded';
      date = undefined;
    } else {
      key = 'generated';
      label = 'Generated';
      date = undefined;
    }

    if (!groupMap.has(key)) {
      groupMap.set(key, { key, label, date, items: [] });
    }
    const group = groupMap.get(key)!;
    group.items.push(item);

    // Keep the earliest date for pipeline/clip groups
    if (
      (key.startsWith('pipeline:') || key.startsWith('clip:')) &&
      item.created_at &&
      (!group.date || item.created_at < group.date)
    ) {
      group.date = item.created_at;
    }
  }

  // Sort: pipeline groups first (date desc), stray clip groups, Generated, Uploaded
  const order = (g: MediaGroup): number => {
    if (g.key.startsWith('pipeline:')) return 0;
    if (g.key.startsWith('clip:')) return 1;
    if (g.key === 'generated') return 2;
    return 3;
  };

  return Array.from(groupMap.values()).sort((a, b) => {
    const oa = order(a);
    const ob = order(b);
    if (oa !== ob) return oa - ob;
    // Within same order: most recent first
    if (a.date && b.date) return a.date > b.date ? -1 : 1;
    return 0;
  });
}
